import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Camera } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useEquipmentPickups } from '@/hooks/useEquipmentPickups';
import { EquipmentDetailModal } from './EquipmentDetailModal';

export const EquipmentAlertBanner: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPickupId, setSelectedPickupId] = useState<string | null>(null);
  const { pickups, loading, fetchPending } = useEquipmentPickups();

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  if (loading || pickups.length === 0) return null;

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `il y a ${minutes}min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `il y a ${hours}h`;
    return `il y a ${Math.floor(hours / 24)}j`;
  };

  return (
    <>
      <div className="bg-amber-50 border-2 border-amber-300 rounded-lg overflow-hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-3 text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span className="font-semibold text-amber-900">
              {pickups.length} équipement{pickups.length > 1 ? 's' : ''} à récupérer
            </span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-amber-700" />
          ) : (
            <ChevronDown className="h-4 w-4 text-amber-700" />
          )}
        </button>

        {isOpen && (
          <div className="px-3 pb-3 space-y-2">
            {pickups.map((pickup) => (
              <button
                key={pickup.id}
                onClick={() => setSelectedPickupId(pickup.id)}
                className="w-full bg-white rounded-lg p-3 border border-amber-200 text-left hover:bg-amber-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{pickup.project_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {pickup.employee_name} — {timeAgo(pickup.created_at)}
                    </div>
                    {pickup.description && (
                      <div className="text-xs text-gray-600 mt-1 truncate">
                        {pickup.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {pickup.photo_count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <Camera className="h-3 w-3 mr-1" />
                        {pickup.photo_count}
                      </Badge>
                    )}
                    <span className="text-muted-foreground">›</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPickupId && (
        <EquipmentDetailModal
          open={!!selectedPickupId}
          onClose={() => setSelectedPickupId(null)}
          pickup={pickups.find((p) => p.id === selectedPickupId)!}
          onResolved={() => {
            setSelectedPickupId(null);
            fetchPending();
          }}
        />
      )}
    </>
  );
};
